import { Cloud, CloudOff, LoaderCircle, TriangleAlert, Check, Ban } from "lucide-react";
import { formatTimestamp } from "@brainlog/shared";
import type { CollabEntry } from "./collab";
import { cn } from "@/lib/utils";

/** Text-first save/sync indicator (never colour alone). */
export function SyncStatus({ entry }: { entry: CollabEntry }) {
  const map: Record<string, { label: string; icon: React.ReactNode; cls: string; detail?: string }> = {
    connecting: { label: "Connecting…", icon: <LoaderCircle className="size-3.5 animate-spin" />, cls: "text-muted-foreground" },
    synced: { label: "All changes synced", icon: <Cloud className="size-3.5" />, cls: "text-muted-foreground" },
    saving: { label: "Saving…", icon: <LoaderCircle className="size-3.5 animate-spin" />, cls: "text-muted-foreground" },
    saved: {
      label: "Saved",
      icon: <Check className="size-3.5" />,
      cls: "text-success",
      detail: entry.lastSavedAt ? `Saved to the server at ${formatTimestamp(entry.lastSavedAt)}` : undefined,
    },
    offline: {
      label: "Offline — edits kept in this tab",
      icon: <CloudOff className="size-3.5" />,
      cls: "text-warning",
      detail: "The connection dropped. Keep this tab open; your edits will sync when it reconnects.",
    },
    "save-error": { label: "Not saved — server error", icon: <TriangleAlert className="size-3.5" />, cls: "text-destructive", detail: entry.error ?? undefined },
    forbidden: { label: "No access", icon: <Ban className="size-3.5" />, cls: "text-destructive", detail: entry.error ?? undefined },
    revoked: { label: "Access removed — read-only", icon: <Ban className="size-3.5" />, cls: "text-destructive", detail: entry.error ?? undefined },
  };
  const s = map[entry.state] ?? map.connecting;
  return (
    <span role="status" aria-live="polite" title={s.detail ?? s.label} className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs", s.cls)} data-sync-state={entry.state}>
      {s.icon}
      {s.label}
    </span>
  );
}
